import "./App.css";
import "./assets/css/style.css";
import "react-quill/dist/quill.snow.css";
import { BrowserRouter } from "react-router-dom";
import { useEffect, useRef, useCallback, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { AuthProvider } from "./context/AuthContext";
import AuthContext from "./context/AuthContext"; // Import AuthContext
import { MFAProvider } from "./context/MFAContext"; // Add this import
import { ToastContextProvider } from "./context/ToastContext";
import CombineRoutes from "routes/CombineRoutes";
import { uniqueProtectedRoutes } from "routes/ProtectedRoutes"; // Import protected routes
import { apiGet } from "./utils/apiFetch";
import apiPath from "./utils/apiPath";

// Auto-logout configuration
const AUTO_LOGOUT_TIME = 60 * 60 * 1000; // 60 minutes in milliseconds
const ACTIVITY_EVENTS = [
  "mousedown",
  "keydown",
  "scroll",
  "touchstart",
  "mousemove",
  "click",
  "wheel",
];
const PUBLIC_ROUTES = [
  "/",
  "/login",
  "/hr-login",
  "/accountant-login",
  "/forgot-password",
  "/company-reset-password",
];

// Wrapper component to handle auto-logout logic
function AppContent({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logoutUser } = useContext(AuthContext); // Get user and logoutUser from context

  const timerRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const isAuthRef = useRef(!!localStorage.getItem("token"));

  // Update isAuthRef when user changes
  useEffect(() => {
    isAuthRef.current = !!localStorage.getItem("token");
  }, [user]);

  const isPublicRoute = useCallback((path) => {
    return PUBLIC_ROUTES.some(
      (route) => path === route || path.startsWith(route + "/"),
    );
  }, []);

  const isProtectedRoute = useCallback((path) => {
    // Check if the current path matches any protected route
    return uniqueProtectedRoutes.some(
      (route) => path === route || path.startsWith(route + "/"),
    );
  }, []);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const showNextLogoutTime = useCallback(() => {
    if (isAuthRef.current && isProtectedRoute(location.pathname)) {
      const logoutTime = new Date(
        Date.now() + AUTO_LOGOUT_TIME,
      ).toLocaleTimeString();
      // console.log(`⏰ Next auto-logout at: ${logoutTime}`);
    }
  }, [location.pathname, isProtectedRoute]);

  const handleLogout = useCallback(() => {
    if (typeof window !== "undefined") {
      // Clear timer first
      clearTimer();
      isAuthRef.current = false;
      logoutUser(); // This would also navigate, so we need to be careful
    }
  }, [navigate, clearTimer, location.pathname, user?.role]);

  const resetTimer = useCallback(() => {
    if (!isAuthRef.current) {
      return;
    }

    if (!isProtectedRoute(location.pathname)) {
      return;
    }

    clearTimer();

    timerRef.current = setTimeout(() => {
      if (isProtectedRoute(location.pathname) && isAuthRef.current) {
        handleLogout();
      }
    }, AUTO_LOGOUT_TIME);

    showNextLogoutTime();
  }, [
    clearTimer,
    location.pathname,
    isProtectedRoute,
    showNextLogoutTime,
    handleLogout,
  ]);

  const handleActivity = useCallback(() => {
    if (isAuthRef.current && isProtectedRoute(location.pathname)) {
      lastActivityRef.current = Date.now();
      resetTimer();
    }
  }, [resetTimer, location.pathname, isProtectedRoute]);

  // Check authentication status on mount and route changes
  useEffect(() => {
    const token = localStorage.getItem("token");
    isAuthRef.current = !!token;

    // Clean up previous listeners
    ACTIVITY_EVENTS.forEach((e) => {
      window.removeEventListener(e, handleActivity);
    });

    if (isAuthRef.current && isProtectedRoute(location.pathname)) {
      lastActivityRef.current = Date.now();
      resetTimer();

      // Set up activity listeners
      ACTIVITY_EVENTS.forEach((e) => {
        window.addEventListener(e, handleActivity);
      });

      // Handle tab visibility change
      const handleVisibilityChange = () => {
        if (
          !document.hidden &&
          isAuthRef.current &&
          isProtectedRoute(location.pathname)
        ) {
          const timeAway = Date.now() - lastActivityRef.current;
          const minutesAway = (timeAway / 60000).toFixed(1);

          if (timeAway > AUTO_LOGOUT_TIME) {
            handleLogout();
          } else {
            resetTimer();
          }
        }
      };

      document.addEventListener("visibilitychange", handleVisibilityChange);

      // Store for cleanup
      return () => {
        clearTimer();
        ACTIVITY_EVENTS.forEach((e) => {
          window.removeEventListener(e, handleActivity);
        });
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange,
        );
      };
    } else if (isAuthRef.current && !isProtectedRoute(location.pathname)) {
      clearTimer();
      return () => {
        clearTimer();
      };
    } else {
      clearTimer();
      return () => {
        clearTimer();
      };
    }
  }, [
    location.pathname,
    resetTimer,
    handleActivity,
    clearTimer,
    handleLogout,
    isProtectedRoute,
    isPublicRoute,
    user?.role,
  ]);

  // GEO ACCESS: ping while logged in so a missing/changed IP expires the session.
  useEffect(() => {
    if (isPublicRoute(location.pathname)) return undefined;
    const role = user?.role;
    const pingPath =
      role === "HRAdmin"
        ? apiPath.getDashboardHRDetails
        : role === "AccountantAdmin"
          ? apiPath.getDashboardAccountantDetails
          : apiPath.getDashboardDetails;
    const ping = () => {
      if (!localStorage.getItem("token")) return;
      apiGet(pingPath, {}, { skipLoader: true }).catch(() => {});
    };
    if (!localStorage.getItem("token")) return undefined;
    ping();
    const id = setInterval(ping, 1800000);
    return () => clearInterval(id);
  }, [location.pathname, isPublicRoute, user?.role]);

  return children;
}

// Wrapper to provide router context to AppContent
function AppWithRouter({ children }) {
  return (
    <BrowserRouter
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <ToastContextProvider>
        <AuthProvider>
          <MFAProvider>
            <AppContent>{children}</AppContent>
          </MFAProvider>
        </AuthProvider>
      </ToastContextProvider>
    </BrowserRouter>
  );
}

function App() {
  return (
    <AppWithRouter>
      <CombineRoutes />
    </AppWithRouter>
  );
}

export default App;
